import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { tokens, Text, Link, Button, Image } from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { NewSignupHeader } from '@bingads-webui-campaign-react/new-signup-header';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { Badge } from '@bingads-webui-fluent-component/spot-illustration';
import { ReactTemplate } from '@bingads-webui-react/react-template';

import msIcon from '../media/btn-microsoft.svg';
import googleIcon from '../media/btn-google.svg';
import facebookIcon from '../media/btn-facebook.svg';

// TODO: [yaoyao] Server side api is not ready yet, so we may need to change the logic later.
const idpKey = 'idP';
const usernameKey = 'username';
const emailKey = 'email';
const scenarioKey = 'scenario';

const params = new URLSearchParams(window.location.search);
const idp = params.get(idpKey) || 'MSA';
const username = params.get(usernameKey);
const email = params.get(emailKey);
const scenario = params.get(scenarioKey);

const getRedirectUrl = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const encodedUrl = urlParams.get('cb') || '';
  return decodeURIComponent(encodedUrl);
};

const getRURL = (search = window.location.search): string => {
  const urlParams = new URLSearchParams(search);
  const match = Array.from(urlParams.entries()).find(([key]) => key.toLowerCase() === 'rurl');
  return match ? match[1] : '';
};

const setIdP = (url: string, provider: string): string => {
  let urlObj: URL;
  try {
    urlObj = new URL(url);
  } catch {
    urlObj = new URL(window.location.href);
  }

  urlObj.searchParams.set('idP', provider);
  return urlObj.toString();
};

const removeIdP = (url: string): string => {
  let urlObj: URL;
  try {
    urlObj = new URL(url);
  } catch {
    urlObj = new URL(window.location.href);
  }

  urlObj.searchParams.delete('idP');
  return urlObj.toString();
};

const continueWithExistingButtonConfig = {
  MSA: {
    icon: msIcon,
    altText: _TL_('Microsoft icon'),
    text: _TL_('Continue with Microsoft'),
    className: 'continue-button-ms',
  },
  Google: {
    icon: googleIcon,
    altText: _TL_('Google icon'),
    text: _TL_('Continue with Google'),
    className: 'continue-button-google',
  },
  Facebook: {
    icon: facebookIcon,
    altText: _TL_('Facebook icon'),
    text: _TL_('Continue with Facebook'),
    className: 'continue-button-facebook',
  },
};

// New sign-in copy rendered for the sign-in scenario. `{{email}}{{/email}}` wraps the email.
export const newSignInTextConfig = {
  MSA: {
    title: _TL_('Use Microsoft sign-in for this email'),
    description: _TL_('To access Microsoft Advertising with {{email}}{{/email}}, sign in with Microsoft.'),
    buttonText: _TL_('Sign in with Microsoft'),
  },
  Google: {
    title: _TL_('Use Google sign-in for this email'),
    description: _TL_('To access Microsoft Advertising with {{email}}{{/email}}, sign in with Google.'),
    buttonText: _TL_('Sign in with Google'),
  },
};

const getPageStyles = () => ({
  idpErrorPage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    minHeight: '100vh',
    height: 'auto',
    backgroundColor: '#faf9f8',
  },
  header: {
    width: '100%',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 1000,
    '& #help-button': {
      display: 'none',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '112px',
    width: '100%',
  },
  dialog: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px',
    width: '80%',
    maxWidth: '744px',
    minWidth: '552px',
    gap: '28px',
    borderRadius: '4px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  staffIcon: {
    width: '128px',
    height: '128px',
  },
  description: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  continueButton: {
    display: 'flex',
    justifyContent: 'flex-start !important',
    gap: '8px',
    minWidth: '280px !important',
  },
  buttonText: {
    padding: '0 24px',
    display: 'flex',
    justifyContent: 'center',
    flex: 1,
  },
  useDifferentEmailLink: {
    fontWeight: '600 !important',
  },
});

export function IdpErrorPage({
  signUpIdpSelectionUrl,
  logger,
}) {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getPageStyles);
  const serverContext = useContext(ServerContext) as any;
  const {
    Urls: {
      FederatedLogin: signinUrl,
      PMaxLiteSignup: signupUrl,
    } = {},
  } = serverContext;

  const {
    icon,
    altText,
    text,
    className,
  // If the idp is not in the continueWithExistingButtonConfig, use the default MSA config
  // This value is from server identityProvider object, it can be AAD which is also Microsoft.
  // If we need to support more 3rd party idps, we will need to update continueWithExistingButtonConfig.
  } = continueWithExistingButtonConfig[idp] || continueWithExistingButtonConfig.MSA;

  const isSignin = scenario === 'signin';
  const newSignInConfig = newSignInTextConfig[idp] || newSignInTextConfig.MSA;

  const onClickContinue = useCallback(() => {
    logger.trace(`Click continue with existing account button. idp: ${idp}`);

    if (['signin', 'signup'].includes(scenario)) {
      let url;
      let emailParamKey;
      if (scenario === 'signin') {
        url = signinUrl;
        // This is a special ask from server side
        // If this button is used for login scenario, the url key should be 'username' even it ss actually an email.
        emailParamKey = 'username';
      } else {
        url = signupUrl;
        emailParamKey = 'email';
      }
      const urlObj = new URL(url);

      if (scenario === 'signin') {
        const rurl = getRURL();
        if (rurl) {
          try {
            // If RURL is set, update the signin URL to redirect to it after successful login
            const successfulLoginUrl = new URL(urlObj.searchParams.get('RURL'));
            successfulLoginUrl.searchParams.set('RURL', rurl);

            urlObj.searchParams.set('RURL', successfulLoginUrl.toString());
          } catch (e) {
            // do nothing
          }
        }
      }

      urlObj.searchParams.set(idpKey, idp);
      urlObj.searchParams.set(emailParamKey, email);

      window.open(urlObj.toString(), '_self');
    } else {
      const redirectUrlWithIdP = setIdP(getRedirectUrl(), idp);

      window.open(redirectUrlWithIdP, '_self');
    }
  }, [logger, signinUrl, signupUrl]);

  const useDifferentEmailLink = useMemo(() => {
    if (['signin', 'signup'].includes(scenario)) {
      let baseUrl;
      if (scenario === 'signin') {
        baseUrl = new URL(signinUrl).origin;
      } else {
        baseUrl = signUpIdpSelectionUrl;
      }

      const newParams = new URLSearchParams(location.search);

      newParams.delete(idpKey);
      newParams.delete(usernameKey);
      newParams.delete(emailKey);
      newParams.delete(scenarioKey);
      const newSearch = newParams.toString();
      const newUrl = newSearch
        ? `${baseUrl}?${newSearch}`
        : baseUrl;

      return newUrl;
    }

    const redirectUrlNoIdP = removeIdP(getRedirectUrl());

    return redirectUrlNoIdP;
  }, [signinUrl, signUpIdpSelectionUrl]);

  const titleText = isSignin
    ? i18n.getString(newSignInConfig.title)
    : i18n.getString(_TL_('Welcome back, {{username}}'), { username });

  const descriptionContent = isSignin ? (
    <div>
      <ReactTemplate
        model={{
          email: () => (
            <strong>
              {email}
            </strong>
          ),
        }}
        template={i18n.getString(newSignInConfig.description)}
      />
    </div>
  ) : (
    <>
      <div>
        <ReactTemplate
          model={{
            a: () => (
              <strong>
                {email}
              </strong>
            ),
          }}
          template={i18n.getString(_TL_('An account already exists with {{a}}{{/a}}.'))}
        />
      </div>
      <Text size={300} >{i18n.getString(_TL_('To either view accounts or create new accounts, please try the following options:'))}</Text>
    </>
  );

  const buttonLabel = isSignin ? i18n.getString(newSignInConfig.buttonText) : i18n.getString(text);

  return (
    <div className={`idp-error-page ${classes.idpErrorPage}`}>
      <div className={`idp-error-page-header ${classes.header}`}>
        <NewSignupHeader
          showMeControl={false}
        />
      </div>
      <div className={`idp-error-page-content ${classes.content}`}>
        <div className={`idp-error-page-dialog ${classes.dialog}`}>
          <Badge
            className="idp-error-page-staff-icon"
            height={128}
            width={128}
          />
          <Text className="idp-error-page-title" size={600} weight="bold">{titleText}</Text>
          <div className={`idp-error-page-description  ${classes.description}`}>
            {descriptionContent}
          </div>
          <Button
            className={`continue-button ${classes.continueButton} ${className}`}
            onClick={onClickContinue}
          >
            <Image
              src={icon}
              alt={i18n.getString(altText)}
            />
            <div className={classes.buttonText}>
              {buttonLabel}
            </div>
          </Button>
          <Link inline className={`use-different-email-link ${classes.useDifferentEmailLink}`} href={useDifferentEmailLink} appearance="subtle">
            {i18n.getString(_TL_('Use a different email'))}
          </Link>
        </div>
      </div>
    </div>
  );
}

IdpErrorPage.propTypes = {
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
  signUpIdpSelectionUrl: PropTypes.string.isRequired,
};

IdpErrorPage.defaultProps = {
  logger: {
    trace: () => {},
  },
};

